import { useEffect, useRef, useCallback } from 'react'

export interface ShortcutActions {
  toggleMic: () => void
  toggleCam: () => void
  toggleHand: () => void
  toggleChat: () => void
  toggleParticipants: () => void
  toggleScreenShare: () => void
  toggleFullscreen: () => void
  closePanels: () => void
  showShortcutsHelp: () => void
  // Push-to-talk: unmute on keydown, remute on keyup
  pushToTalkStart: () => void
  pushToTalkEnd: () => void
}

export interface ShortcutDef {
  keys: string
  label: string
  category: string
}

export const SHORTCUT_LIST: ShortcutDef[] = [
  { keys: 'Alt+M', label: 'Activer/couper le micro', category: 'Audio' },
  { keys: 'Ctrl+D', label: 'Activer/couper le micro', category: 'Audio' },
  { keys: 'Space (maintenu)', label: 'Push-to-talk (maintenir pour parler)', category: 'Audio' },
  { keys: 'Alt+V', label: 'Activer/couper la camera', category: 'Video' },
  { keys: 'Ctrl+E', label: 'Activer/couper la camera', category: 'Video' },
  { keys: 'Alt+S', label: 'Partager/arreter le partage d\'ecran', category: 'Video' },
  { keys: 'Alt+F', label: 'Plein ecran', category: 'Affichage' },
  { keys: 'Alt+H', label: 'Lever/baisser la main', category: 'Communication' },
  { keys: 'Alt+C', label: 'Ouvrir/fermer le chat', category: 'Communication' },
  { keys: 'Alt+P', label: 'Panneau participants', category: 'Communication' },
  { keys: 'Escape', label: 'Fermer panneau/menu ouvert', category: 'Navigation' },
  { keys: '?', label: 'Afficher les raccourcis', category: 'Navigation' },
]

function isInputFocused(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select' || (el as HTMLElement).isContentEditable
}

export function useKeyboardShortcuts(actions: ShortcutActions, enabled: boolean) {
  const pttActiveRef = useRef(false)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return
    if (isInputFocused()) return

    // Push-to-talk: Space held
    if (e.code === 'Space' && !e.altKey && !e.ctrlKey && !e.metaKey && !e.repeat) {
      e.preventDefault()
      if (!pttActiveRef.current) {
        pttActiveRef.current = true
        actions.pushToTalkStart()
      }
      return
    }

    // Alt+M or Ctrl+D: toggle mic
    if ((e.altKey && e.key.toLowerCase() === 'm') || (e.ctrlKey && e.key.toLowerCase() === 'd')) {
      e.preventDefault()
      actions.toggleMic()
      return
    }

    // Alt+V or Ctrl+E: toggle cam
    if ((e.altKey && e.key.toLowerCase() === 'v') || (e.ctrlKey && e.key.toLowerCase() === 'e')) {
      e.preventDefault()
      actions.toggleCam()
      return
    }

    // Alt+H: toggle hand
    if (e.altKey && e.key.toLowerCase() === 'h') {
      e.preventDefault()
      actions.toggleHand()
      return
    }

    // Alt+C: toggle chat
    if (e.altKey && e.key.toLowerCase() === 'c') {
      e.preventDefault()
      actions.toggleChat()
      return
    }

    // Alt+P: toggle participants
    if (e.altKey && e.key.toLowerCase() === 'p') {
      e.preventDefault()
      actions.toggleParticipants()
      return
    }

    // Alt+S: toggle screen share
    if (e.altKey && e.key.toLowerCase() === 's') {
      e.preventDefault()
      actions.toggleScreenShare()
      return
    }

    // Alt+F: toggle fullscreen
    if (e.altKey && e.key.toLowerCase() === 'f') {
      e.preventDefault()
      actions.toggleFullscreen()
      return
    }

    // Escape: close panels/menus
    if (e.key === 'Escape') {
      actions.closePanels()
      return
    }

    // ?: show shortcuts help
    if (e.key === '?' && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      actions.showShortcutsHelp()
      return
    }
  }, [enabled, actions])

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (!enabled) return

    // Push-to-talk: Space released
    if (e.code === 'Space' && pttActiveRef.current) {
      pttActiveRef.current = false
      actions.pushToTalkEnd()
    }
  }, [enabled, actions])

  useEffect(() => {
    if (!enabled) return
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      // Ensure PTT is released if component unmounts while held
      if (pttActiveRef.current) {
        pttActiveRef.current = false
        actions.pushToTalkEnd()
      }
    }
  }, [enabled, handleKeyDown, handleKeyUp, actions])
}
